import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { useField, useFormikContext } from "formik"
import { Box, Drawer, Grid, Stack, Tooltip } from "@mui/material"
import { faCartShopping } from "@fortawesome/pro-solid-svg-icons"
import { ButtonConfigType, StandardOrderTypeFilter } from "../../filters/orderTypeFilter/StandardOrderTypeFilter"
import { CreateOrderDialogFlow, CreateOrderDialogType, SelectedPriceListItem } from "../createOrderTypes"
import { DeliveryTimesField, DynamicConfigKey, DynamicMainFormValues } from "./form/formTypes"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { OrderSummaryWrap } from "../components/drawer/orderSummary/OrderSummaryWrap"
import { NullableCalendarEvent } from "./resource/calendar/fullCalendarTypes"
import { CreateOrderDialogTitle } from "../components/CreateOrderDialogTitle"
import { ResourceBookingControls } from "./resource/ResourceBookingControls"
import { PriceListEditButton } from "./shop/priceLists/PriceListEditButton"
import { OrderContextState } from "../mainForm/helpers"
import { InitialEventState } from "./resource/useInitialEventState"
import { DRAWER_WIDTH } from "./drawer/CreateOrderDialogDrawer"
import { IconBadge } from "../../badges/IconBadge"
import { CreateOrderType } from "../helpers"

const useStyles = makeStyles()((_theme) => ({
    drawer: {
        zIndex: "9999",
    },
    container: {
        overflow: "auto",
    },
    drawerContent: {
        flexGrow: 1,
        padding: "1rem",
        justifyContent: "space-between",
        width: DRAWER_WIDTH,
    },
    drawerCart: {
        marginRight: "0.75rem",
    },
    drawerTitle: {
        maxWidth: "24.5rem",
    },
}))

type CreateOrderDialogTitleWrapProps = Readonly<{
    handleClose: () => void
    orderType: CreateOrderType
    handleChangeOrderType: (type: CreateOrderType) => void
    filteredTabButtonConfig: ButtonConfigType<CreateOrderType>[]
    dialogType: CreateOrderDialogType
    initialEventState: InitialEventState
    handleCancelClick: () => void
    confirmBookingDialog: boolean
    setConfirmBookingDialog: Dispatch<SetStateAction<boolean>>
    handleOpenSelectPriceListsDialog: () => void
    flowConfig?: CreateOrderDialogFlow
    orderContext: OrderContextState
}>

export function CreateOrderDialogTitleWrap({
    handleClose,
    orderType,
    handleChangeOrderType,
    filteredTabButtonConfig,
    dialogType,
    initialEventState,
    handleCancelClick,
    confirmBookingDialog,
    setConfirmBookingDialog,
    handleOpenSelectPriceListsDialog,
    flowConfig,
    orderContext,
}: CreateOrderDialogTitleWrapProps) {
    const { classes } = useStyles()
    const { dirty } = useFormikContext<DynamicMainFormValues>()
    const isRefundOrder = dialogType === CreateOrderDialogType.CREDIT_NOTE

    const [{ value: deliveries }] = useField<DeliveryTimesField | undefined>(DynamicConfigKey.MULTIPLE_DELIVERIES)
    const [{ value: priceListItem }] = useField<SelectedPriceListItem | null>("priceList")
    const [{ value: event }] = useField<NullableCalendarEvent>("event")

    const [drawerOpen, setDrawerOpen] = useState(false)
    const hasAutoOpenedRef = useRef(false)

    useEffect(() => {
        const hasDeliveries = !!deliveries?.deliveryTimes?.length

        if (!hasAutoOpenedRef.current && isRefundOrder && hasDeliveries) {
            setDrawerOpen(true)
            hasAutoOpenedRef.current = true
        }
    }, [isRefundOrder, deliveries])

    const resourcesCount = event?.resourceIds.length || 0

    const deliveriesCount = useMemo(() => {
        if (!deliveries?.deliveryTimes?.length) return 0

        return deliveries.deliveryTimes.reduce((acc, slot) => {
            const lines = slot?.orderLines ?? []
            const sumInSlot = lines.reduce((s, line: any) => s + (Number(line?.quantity) || 0), 0)
            return acc + sumInSlot
        }, 0)
    }, [deliveries])

    const badgeCount = resourcesCount + deliveriesCount

    const handleBookClick = useCallback(() => {
        handleCloseDrawer()
        setConfirmBookingDialog(true)
    }, [])

    const handleDrawerOpen = () => {
        setDrawerOpen(true)
    }

    const handleCloseDrawer = () => {
        setDrawerOpen(false)
    }

    return (
        <>
            <CreateOrderDialogTitle handleClose={handleClose} isDirtyForm={dirty} flowConfig={flowConfig}>
                <Grid container flexWrap="nowrap" alignItems="center" flexDirection="row" gap="1rem">
                    <StandardOrderTypeFilter
                        orderType={orderType}
                        setOrderType={handleChangeOrderType}
                        config={filteredTabButtonConfig}
                        isMarginBottom={false}
                        isLight={false}
                    />
                    {orderType === CreateOrderType.MEETING_SERVICE && priceListItem && (
                        <PriceListEditButton
                            selectedPriceList={priceListItem}
                            handleOpenSelectPriceListsDialog={handleOpenSelectPriceListsDialog}
                            orderContext={orderContext}
                            isDisabledEdit={dialogType !== CreateOrderDialogType.ORDER}
                        />
                    )}
                </Grid>
                <LocalizedStrict id="create-order-tooltip-order-summary-button" attrs={{ title: true }}>
                    <Tooltip title="View order summary">
                        <div>
                            <IconBadge
                                variant="font-awesome"
                                color="blue"
                                badgeValue={badgeCount}
                                fontAwesomeIcon={faCartShopping}
                                className={classes.drawerCart}
                                onClick={handleDrawerOpen}
                            />
                        </div>
                    </Tooltip>
                </LocalizedStrict>
            </CreateOrderDialogTitle>
            {drawerOpen && (
                <Drawer anchor="right" open={drawerOpen} onClose={handleCloseDrawer} className={classes.drawer}>
                    <Stack gap="1.5rem" className={classes.drawerContent}>
                        <Box className={classes.container}>
                            <OrderSummaryWrap
                                isRefundOrder={isRefundOrder}
                                handleCloseDrawer={handleCloseDrawer}
                                isUpdatable={orderType === CreateOrderType.RESOURCE_BOOKING}
                                orderType={orderType}
                            />
                        </Box>
                        {orderType === CreateOrderType.RESOURCE_BOOKING && (
                            <ResourceBookingControls
                                initialEventState={initialEventState}
                                onCancel={handleCancelClick}
                                onBook={handleBookClick}
                                isFixed={false}
                                isLoadingButton={confirmBookingDialog}
                                flow={flowConfig?.flow}
                            />
                        )}
                    </Stack>
                </Drawer>
            )}
        </>
    )
}
